/**
 * Tenant Users Service
 *
 * Service for managing users within a specific tenant.
 * Users are stored in the global userProfiles collection with companyId reference.
 * Tenant config (including user cap) stored in tenants/{tenantId}/config
 */

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { FirebaseService } from '../../../shared/services/FirebaseService';

class TenantUsersServiceClass {
  constructor() {
    this.db = null;
    this.auth = null;
    this.tenantId = null;
    this.initialized = false;
    console.log('🔧 TenantUsersService initialized');
  }

  /**
   * Set the current tenant context
   * @param {string} tenantId - The tenant ID
   */
  setTenant(tenantId) {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }
    this.tenantId = tenantId;
    console.log(`🏢 TenantUsersService tenant set to: ${tenantId}`);
  }

  /**
   * Get current tenant ID
   */
  getTenantId() {
    if (!this.tenantId) {
      throw new Error('Tenant not set. Call setTenant(tenantId) first.');
    }
    return this.tenantId;
  }

  /**
   * Initialize Firestore and Auth connections
   */
  async initialize() {
    if (this.initialized && this.db) return;

    try {
      await FirebaseService.initialize();
      this.db = FirebaseService.getFirestore();
      this.auth = FirebaseService.getAuth();
      this.initialized = true;
      console.log('✅ TenantUsersService initialized');
    } catch (error) {
      console.error('❌ Failed to initialize TenantUsersService:', error);
      throw error;
    }
  }

  /**
   * Get tenant configuration (including user cap)
   */
  async getTenantConfig() {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      const docRef = doc(this.db, `tenants/${tenantId}/config/settings`);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        // Return default config
        return {
          userCap: 5, // Default user limit
          name: tenantId,
          createdAt: null
        };
      }

      return docSnap.data();
    } catch (error) {
      console.error('❌ Failed to get tenant config:', error);
      return { userCap: 5, name: tenantId };
    }
  }

  /**
   * Update tenant configuration
   */
  async updateTenantConfig(config) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      const docRef = doc(this.db, `tenants/${tenantId}/config/settings`);
      await setDoc(docRef, {
        ...config,
        updated_at: serverTimestamp()
      }, { merge: true });

      console.log('✅ Tenant config updated');
      return config;
    } catch (error) {
      console.error('❌ Failed to update tenant config:', error);
      throw error;
    }
  }

  /**
   * Get all users for the current tenant
   * @returns {Array} Array of users
   */
  async getTenantUsers() {
    await this.initialize();
    const tenantId = this.getTenantId();

    // CHA-97 / PR-C: direct userProfiles read is blocked at the rules
    // layer. Routes through the listUsers callable which composes
    // roleAssignments + Firebase Auth identity. Returned shape:
    //   { uid, email, displayName, disabled, emailVerified,
    //     roleId, tenantId, assignedBy, assignedAt, _orphan? }
    console.log(`👥 Fetching users for tenant ${tenantId} via listUsers callable`);
    const fn = httpsCallable(getFunctions(), 'listUsers');
    const result = await fn({ tenantId });
    return result.data?.users || [];
  }

  /**
   * Get user count for the tenant
   */
  async getUserCount() {
    const users = await this.getTenantUsers();
    return users.length;
  }

  /**
   * Check if tenant can add more users
   */
  async canAddUser() {
    const [config, count] = await Promise.all([
      this.getTenantConfig(),
      this.getUserCount()
    ]);
    return count < (config.userCap || 5);
  }

  /**
   * Create a pending user invitation for the tenant.
   *
   * CHA-97 / PR-C (Decision α): the synthetic `pending_<ts>` userProfile
   * stub is no longer written. The `user_invitations` doc IS the durable
   * pending-invitation record; the real `userProfiles` / `roleAssignments`
   * pair gets created when the invitee accepts (via the
   * acceptUserInvitation flow → saveUserProfile → assignUserRole callable).
   *
   * Two shape conflicts the stub used to introduce that this fix
   * eliminates:
   *   1. The pending UID isn't a real Firebase Auth UID — the
   *      mintClaimsOnRoleAssignmentWrite trigger would skip any
   *      roleAssignments doc written under it (`isRealAuthUid` check).
   *   2. Default role 'viewer' isn't in `ALLOWED_TENANT_ROLES` server-side
   *      — assignUserRole would reject. (The role is preserved on the
   *      invitation doc and validated at accept-time.)
   *
   * @param {Object} userData - User data (email, name, role)
   * @param {string} tempPassword - Temporary password
   * @returns {Object} the invitation record (durable pending state)
   */
  async createUser(userData, tempPassword) {
    await this.initialize();
    const tenantId = this.getTenantId();

    try {
      console.log(`👤 Creating user invitation for tenant ${tenantId}:`, userData.email);

      // Check user cap
      const canAdd = await this.canAddUser();
      if (!canAdd) {
        throw new Error('User limit reached. Contact platform admin to increase limit.');
      }

      const currentUser = FirebaseService.getCurrentUser();

      // CHA-97 / PR-C (Decision α): pending userProfile stub removed.
      // The invitation record below is the only durable representation
      // of the pending invite. acceptUserInvitation will materialize the
      // real userProfiles + roleAssignments docs via the server-controlled
      // callable when the invitee accepts.
      const invitation = {
        email: userData.email.toLowerCase(),
        tenantId: tenantId,
        role: userData.role || 'viewer',
        name: userData.name || '',
        status: 'pending',
        tempPassword: tempPassword, // Store temporarily - user will reset
        createdBy: currentUser?.email || 'unknown',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
      };

      const invitationRef = doc(this.db, 'user_invitations', userData.email.toLowerCase());
      await setDoc(invitationRef, invitation);

      console.log(`✅ User invitation created: ${userData.email}`);
      return invitation;

    } catch (error) {
      console.error('❌ Failed to create user invitation:', error);
      throw new Error(`Failed to create user invitation: ${error.message}`);
    }
  }

  /**
   * Update a user's role.
   *
   * CHA-97 / PR-C: routes through the assignUserRole callable. Non-role
   * fields in `updates` (name, etc.) are silently dropped per Decision
   * D-4 — RoutingDashboard's edit form passes `{ name, role }` and only
   * role persists. Name editing is disabled at the UI layer (CHA-117
   * follow-up restores it via a future profile-fields surface).
   *
   * setUserDisabled no longer routes through this method — it goes
   * directly to the setUserStatus callable.
   *
   * @param {string} userId - User ID
   * @param {Object} updates - Fields to update (only `role` is acted on)
   * @returns {Promise<object>} { ok, targetUid, tenantId, roleId }
   */
  async updateUser(userId, updates) {
    await this.initialize();
    const tenantId = this.getTenantId();

    const roleId = updates?.roleId || updates?.role;
    if (!roleId) {
      throw new Error('updateUser requires a role on updates (legacy field name) or roleId');
    }

    console.log(`👤 Updating user ${userId} role to ${roleId} (tenant: ${tenantId})`);
    const functions = getFunctions();
    const assignUserRole = httpsCallable(functions, 'assignUserRole');
    const result = await assignUserRole({
      targetUid: userId,
      tenantId,
      roleId,
    });
    return result.data;
  }

  /**
   * Disable/enable a user.
   *
   * CHA-97 / PR-C: routes through the setUserStatus callable. The
   * tenantId comes from internal context (this.getTenantId()).
   *
   * @param {string} userId
   * @param {boolean} disabled
   * @returns {Promise<object>} { ok, targetUid, tenantId, status }
   */
  async setUserDisabled(userId, disabled) {
    await this.initialize();
    const tenantId = this.getTenantId();
    const status = disabled ? 'inactive' : 'active';

    console.log(`🔒 Setting user ${userId} status to ${status} (tenant: ${tenantId})`);
    const functions = getFunctions();
    const setUserStatus = httpsCallable(functions, 'setUserStatus');
    const result = await setUserStatus({
      targetUid: userId,
      tenantId,
      status,
    });
    return result.data;
  }

  /**
   * Delete a user (remove from tenant).
   *
   * CHA-97 / PR-C: routes through the deleteUser callable. Atomically
   * removes both userProfiles AND roleAssignments docs server-side.
   * Firebase Auth user is NOT deleted (per CHA-118 deferral).
   *
   * @param {string} userId
   * @returns {Promise<object>} { ok, targetUid, tenantId }
   */
  async deleteUser(userId) {
    await this.initialize();
    const tenantId = this.getTenantId();

    console.log(`🗑️ Deleting user ${userId} (tenant: ${tenantId})`);
    const functions = getFunctions();
    const deleteUser = httpsCallable(functions, 'deleteUser');
    const result = await deleteUser({
      targetUid: userId,
      tenantId,
    });
    return result.data;
  }

  /**
   * Send password reset email
   * @param {string} email - User email
   */
  async sendPasswordReset(email) {
    await this.initialize();

    try {
      await sendPasswordResetEmail(this.auth, email);
      console.log(`📧 Password reset email sent to ${email}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Failed to send password reset:', error);
      throw error;
    }
  }

  /**
   * Get permissions for a role
   * @param {string} role - User role
   * @returns {Object} Permissions object
   */
  getRolePermissions(role) {
    const permissions = {
      admin: {
        viewDashboard: true,
        viewNotes: true,
        editNotes: true,
        uploadData: true,
        manageShipPoints: true,
        manageUsers: true,
        viewSettings: true,
        editSettings: true
      },
      manager: {
        viewDashboard: true,
        viewNotes: true,
        editNotes: true,
        uploadData: true,
        manageShipPoints: false,
        manageUsers: false,
        viewSettings: true,
        editSettings: false
      },
      user: {
        viewDashboard: true,
        viewNotes: true,
        editNotes: true,
        uploadData: false,
        manageShipPoints: false,
        manageUsers: false,
        viewSettings: false,
        editSettings: false
      },
      viewer: {
        viewDashboard: true,
        viewNotes: true,
        editNotes: false,
        uploadData: false,
        manageShipPoints: false,
        manageUsers: false,
        viewSettings: false,
        editSettings: false
      }
    };

    return permissions[role] || permissions.viewer;
  }

}

// Export singleton instance
export const TenantUsersService = new TenantUsersServiceClass();

// Also export class for testing
export { TenantUsersServiceClass };
